import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { CatalogRoom } from '~/types/stopsell';

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: (selectedRates: Record<string, Array<string>>) => void;
  catalogRooms: Array<CatalogRoom>;
  fileName: string;
  description?: string;
}

interface SharedRateGroup {
  name: string;
  entries: Array<{ roomId: string; rateId: string }>;
}

interface RoomRateSection {
  roomId: string;
  roomName: string;
  rates: Array<{ id: string; name: string }>;
  hasNoRatesAtAll: boolean;
}

function initAllSelected(rooms: Array<CatalogRoom>): Record<string, Set<string>> {
  return rooms.reduce<Record<string, Set<string>>>((acc, room) => {
    acc[room.id] = new Set(room.roomRates.map((r) => r.id));
    return acc;
  }, {});
}

function deriveRateSections(catalogRooms: Array<CatalogRoom>): {
  sharedGroups: Array<SharedRateGroup>;
  roomSections: Array<RoomRateSection>;
} {
  const nameToEntries = new Map<string, Array<{ roomId: string; rateId: string }>>();

  for (const room of catalogRooms) {
    for (const rate of room.roomRates) {
      const existing = nameToEntries.get(rate.name) ?? [];
      existing.push({ roomId: room.id, rateId: rate.id });
      nameToEntries.set(rate.name, existing);
    }
  }

  const sharedNames = new Set<string>();
  const sharedGroups: Array<SharedRateGroup> = [];

  for (const [name, entries] of nameToEntries) {
    if (entries.length >= 2) {
      sharedNames.add(name);
      sharedGroups.push({ name, entries });
    }
  }

  const roomSections: Array<RoomRateSection> = catalogRooms
    .map((room) => ({
      roomId: room.id,
      roomName: room.name,
      rates: room.roomRates.filter((r) => !sharedNames.has(r.name)),
      hasNoRatesAtAll: room.roomRates.length === 0,
    }))
    .filter((s) => s.rates.length > 0 || s.hasNoRatesAtAll);

  return { sharedGroups, roomSections };
}

export default function AvailabilityRateSelectModal({
  open,
  onClose,
  onContinue,
  catalogRooms,
  fileName,
  description = 'Choose which room rates to apply stopsell dates to. This applies to both extracted and manually added rooms.',
}: Props) {
  const [selected, setSelected] = useState<Record<string, Set<string>>>(() => initAllSelected(catalogRooms));
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelected(initAllSelected(catalogRooms));
    }
    prevOpenRef.current = open;
  }, [open, catalogRooms]);

  const { sharedGroups, roomSections } = useMemo(() => deriveRateSections(catalogRooms), [catalogRooms]);

  const { totalRateNames, selectedRateNames } = useMemo(() => {
    let total = sharedGroups.length;
    let selectedCount = 0;

    for (const group of sharedGroups) {
      const allSelected = group.entries.every(({ roomId, rateId }) => selected[roomId]?.has(rateId));
      if (allSelected) selectedCount++;
    }

    for (const section of roomSections) {
      total += section.rates.length;
      for (const rate of section.rates) {
        if (selected[section.roomId]?.has(rate.id)) selectedCount++;
      }
    }

    return { totalRateNames: total, selectedRateNames: selectedCount };
  }, [sharedGroups, roomSections, selected]);

  const handleSharedRateToggle = useCallback((entries: Array<{ roomId: string; rateId: string }>, checked: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      for (const { roomId, rateId } of entries) {
        const roomSet = new Set(next[roomId]);
        if (checked) roomSet.add(rateId);
        else roomSet.delete(rateId);
        next[roomId] = roomSet;
      }
      return next;
    });
  }, []);

  const handleRateToggle = useCallback((roomId: string, rateId: string) => {
    setSelected((prev) => {
      const next = new Set(prev[roomId]);
      if (next.has(rateId)) {
        next.delete(rateId);
      } else {
        next.add(rateId);
      }
      return { ...prev, [roomId]: next };
    });
  }, []);

  const handleRoomToggleAll = useCallback((roomId: string, rateIds: Array<string>, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev[roomId]);
      rateIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return { ...prev, [roomId]: next };
    });
  }, []);

  const handleContinue = useCallback(() => {
    const result = Object.fromEntries(
      Object.entries(selected).map(([roomId, rateSet]) => [roomId, Array.from(rateSet)]),
    );
    onContinue(result);
  }, [selected, onContinue]);

  const hasNoRates = sharedGroups.length === 0 && roomSections.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <TuneIcon color="primary" />
          <Typography variant="h6">Select Room Rates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {hasNoRates ? (
          <Typography variant="caption" color="text.disabled">
            No rates configured for this room
          </Typography>
        ) : (
          <Stack gap={3}>
            {sharedGroups.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Shared rates
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Selecting or deselecting applies to all rooms that include this rate.
                </Typography>
                <Stack gap={0.5}>
                  {sharedGroups.map((group) => {
                    const allSelected = group.entries.every(({ roomId, rateId }) => selected[roomId]?.has(rateId));
                    const anySelected = group.entries.some(({ roomId, rateId }) => selected[roomId]?.has(rateId));
                    return (
                      <Box
                        key={group.name}
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <FormControlLabel
                            label={<Typography variant="body2">{group.name}</Typography>}
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={anySelected && !allSelected}
                                onChange={(e) => handleSharedRateToggle(group.entries, e.target.checked)}
                                sx={{ p: 0.25, mr: 0.5 }}
                              />
                            }
                          />
                          <Chip
                            label={`${group.entries.length} rooms`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {roomSections.length > 0 && (
              <Box>
                {sharedGroups.length > 0 && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                      Room-specific rates
                    </Typography>
                  </>
                )}
                <Stack gap={2}>
                  {roomSections.map((section, index) => {
                    const roomSelected = selected[section.roomId] ?? new Set<string>();
                    const rateIds = section.rates.map((r) => r.id);
                    const allChecked = rateIds.length > 0 && rateIds.every((id) => roomSelected.has(id));
                    const anyChecked = rateIds.some((id) => roomSelected.has(id));

                    return (
                      <Box key={section.roomId}>
                        {index > 0 && <Divider sx={{ mb: 2 }} />}

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2">{section.roomName}</Typography>
                          {rateIds.length > 1 && (
                            <FormControlLabel
                              label={<Typography variant="caption">Select all</Typography>}
                              labelPlacement="start"
                              sx={{ mr: 0, gap: 0.5 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={allChecked}
                                  indeterminate={anyChecked && !allChecked}
                                  onChange={(e) => handleRoomToggleAll(section.roomId, rateIds, e.target.checked)}
                                  sx={{ p: 0.25 }}
                                />
                              }
                            />
                          )}
                        </Stack>

                        {section.hasNoRatesAtAll ? (
                          <Typography variant="caption" color="text.disabled">
                            No rates configured for this room
                          </Typography>
                        ) : (
                          <Stack gap={0.5}>
                            {section.rates.map((rate) => (
                              <Box
                                key={rate.id}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <FormControlLabel
                                  label={<Typography variant="body2">{rate.name}</Typography>}
                                  sx={{ m: 0, width: '100%' }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={roomSelected.has(rate.id)}
                                      onChange={() => handleRateToggle(section.roomId, rate.id)}
                                      sx={{ p: 0.25, mr: 0.5 }}
                                    />
                                  }
                                />
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {selectedRateNames} of {totalRateNames} rate{totalRateNames !== 1 ? 's' : ''} selected
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={Object.values(selected).every((s) => s.size === 0)}
          >
            Continue to Review
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
